/**
 * useDynamicBranding — keeps document.title, favicon, theme-color, splash bg
 * and CSS vars in sync with whatever event/school/church the user is viewing.
 *
 * Mount once at the root of <App>. Listens to react-router location, fetches
 * /api/branding/resolve?path=<pathname>, and applies a tenant-specific
 * favicon / title when matched. Reverts to platform default when the route
 * doesn't belong to a tenant context.
 *
 * Plays nicely with the platform-wide AppConfigProvider — uses the same
 * `dynamic-favicon` <link> element so platform default favicon (set by
 * AppConfigProvider) is restored on revert.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const cache = new Map();           // path → branding payload (deduped fetches)
let baseSnapshot = null;           // captured platform defaults

function resolveAssetUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  return url;
}

function captureBase() {
  if (baseSnapshot) return baseSnapshot;
  const fav = document.getElementById('dynamic-favicon') || document.getElementById('favicon-link');
  const apple = document.querySelector("link[rel='apple-touch-icon']");
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  baseSnapshot = {
    title: document.title,
    favicon: fav?.href || '/favicon.ico',
    apple: apple?.href || '',
    themeColor: themeMeta?.content || '',
  };
  return baseSnapshot;
}

function ensureFavLink() {
  let link = document.getElementById('dynamic-favicon');
  if (!link) {
    link = document.createElement('link');
    link.id = 'dynamic-favicon';
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  return link;
}

function applyBranding(brand) {
  captureBase();

  // Title — keep base name as suffix so users always know where they are
  if (brand?.name) {
    const baseName = baseSnapshot.title.split('·').pop().trim() || baseSnapshot.title || 'EventFlow';
    document.title = `${brand.name} · ${baseName}`;
  } else {
    document.title = baseSnapshot.title;
  }

  const link = ensureFavLink();
  if (brand?.favicon_url) {
    const url = resolveAssetUrl(brand.favicon_url);
    link.href = url;
    link.type = url.endsWith('.ico') ? 'image/x-icon' : 'image/png';
    const apple = document.querySelector("link[rel='apple-touch-icon']");
    if (apple) apple.href = url;
  } else if (baseSnapshot.favicon) {
    link.href = baseSnapshot.favicon;
    const apple = document.querySelector("link[rel='apple-touch-icon']");
    if (apple && baseSnapshot.apple) apple.href = baseSnapshot.apple;
  }

  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta) themeMeta.setAttribute('content', brand?.theme_color || baseSnapshot.themeColor || '#0f172a');

  const root = document.documentElement;
  root.style.setProperty('--brand-primary',   brand?.primary_color   || '#6366f1');
  root.style.setProperty('--brand-secondary', brand?.secondary_color || '');
  if (brand?.logo_url) root.style.setProperty('--brand-logo', `url(${JSON.stringify(resolveAssetUrl(brand.logo_url))})`);
  if (brand?.theme_color) root.style.setProperty('--brand-bg', brand.theme_color);
}

export default function useDynamicBranding() {
  const { pathname } = useLocation();
  const last = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (last.current === pathname) return;
    last.current = pathname;

    const apply = (b) => { if (!cancelled) applyBranding(b); };

    // Quick segment heuristic — skip API call entirely for paths that can't
    // be tenant-scoped (login, dashboard root, etc.). Default = revert.
    const seg = pathname.split('/').filter(Boolean);
    const tenantish =
      (seg[0] === 'admin' && seg[1] === 'events' && seg[2]) ||
      (seg[0] === 'event'  && seg[1]) ||
      (seg[0] === 'school' && seg[1]) ||
      (seg[0] === 's' && seg[1]) ||
      (seg[0] === 'c' && seg[1]);
    if (!tenantish) {
      apply(null);
      return () => { cancelled = true; };
    }

    if (cache.has(pathname)) {
      apply(cache.get(pathname));
      return () => { cancelled = true; };
    }

    fetch(`${API_URL}/api/branding/resolve?path=${encodeURIComponent(pathname)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((b) => {
        if (cancelled) return;
        if (!b || b.kind === 'platform') { cache.set(pathname, null); apply(null); return; }
        cache.set(pathname, b);
        apply(b);
      })
      .catch(() => { if (!cancelled) apply(null); });

    return () => { cancelled = true; };
  }, [pathname]);
}

